import React, { useState, useEffect, useMemo } from 'react';
import { getInventories, getUsers, createInventory, updateInventory, deleteInventory } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { 
  Boxes, 
  Plus, 
  Edit, 
  Trash2, 
  Loader2,
  AlertCircle,
  Warehouse,
  Truck
} from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../context/AuthContext';

const Inventories = () => {
  const { isAdmin } = useAuth();
  const [inventories, setInventories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInventory, setEditingInventory] = useState(null);
  const [formData, setFormData] = useState({
    inventory_name: '',
    type: 'warehouse',
    assigned_user_id: '',
    active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const techUsers = useMemo(() => users.filter(u => u.role === 'technician'), [users]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, usersRes] = await Promise.all([
        getInventories(),
        getUsers().catch(() => ({ data: [] }))
      ]);
      setInventories(invRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingInventory(null);
    setFormData({
      inventory_name: '',
      type: 'warehouse',
      assigned_user_id: '',
      active: true
    });
    setError('');
    setDialogOpen(true);
  };

  const openEditDialog = (inventory) => {
    setEditingInventory(inventory);
    setFormData({
      inventory_name: inventory.inventory_name,
      type: inventory.type,
      assigned_user_id: inventory.assigned_user_id || '',
      active: inventory.active
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.inventory_name) {
      setError('Please enter an inventory name');
      return;
    }

    setSaving(true);
    setError('');
    
    try {
      const payload = {
        ...formData,
        assigned_user_id: formData.assigned_user_id || null
      };
      
      if (editingInventory) {
        await updateInventory(editingInventory.id, payload);
      } else {
        await createInventory(payload);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save inventory');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setDeleting(true);
    try {
      await deleteInventory(itemToDelete.id);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete inventory');
    } finally {
      setDeleting(false);
    }
  };

  const getUserName = (id) => {
    if (!id) return 'Not assigned';
    const user = users.find(u => u.id === id);
    return user ? user.name : 'Unknown';
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="inventories-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Inventories</h1>
          <p className="text-[#6B7280]">Manage warehouses and vehicle inventories</p>
        </div>
        <Button onClick={openCreateDialog} className="gap-2 bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="add-inventory-btn">
          <Plus size={18} />
          Add Inventory
        </Button>
      </div>

      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <Boxes size={20} className="text-[#0776BD]" />
            Locations ({inventories.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : inventories.length === 0 ? (
            <div className="py-12 text-center text-[#6B7280]">
              <Boxes size={48} className="mx-auto mb-4 opacity-50" />
              <p>No inventories found</p>
              <p className="text-sm">Click "Add Inventory" to create one</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F6F7]">
                  <TableHead className="font-semibold text-[#1F2937]">Name</TableHead>
                  <TableHead className="font-semibold text-[#1F2937]">Type</TableHead>
                  <TableHead className="font-semibold text-[#1F2937]">Assigned To</TableHead>
                  <TableHead className="font-semibold text-[#1F2937] text-center">Status</TableHead>
                  <TableHead className="font-semibold text-[#1F2937] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inventories.map((inv) => (
                  <TableRow key={inv.id} className="hover:bg-[#F5F6F7]" data-testid={`inventory-row-${inv.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {inv.type === 'warehouse' ? (
                          <div className="p-2 bg-[#E6F2FA] rounded-lg">
                            <Warehouse size={18} className="text-[#0776BD]" />
                          </div>
                        ) : (
                          <div className="p-2 bg-[#F0C500]/20 rounded-lg">
                            <Truck size={18} className="text-[#F0C500]" />
                          </div>
                        )}
                        <span className="font-medium text-[#1F2937]">{inv.inventory_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-[#F5F6F7] text-[#6B7280] border-0 capitalize">
                        {inv.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[#6B7280]">{getUserName(inv.assigned_user_id)}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={inv.active ? "bg-green-100 text-green-800 border-0" : "bg-gray-100 text-gray-600 border-0"}>
                        {inv.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(inv)}
                          className="hover:bg-[#E6F2FA] text-[#0776BD]"
                          data-testid={`edit-inventory-${inv.id}`}
                        >
                          <Edit size={18} />
                        </Button>
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setItemToDelete(inv); setDeleteDialogOpen(true); }}
                            className="hover:bg-red-50 text-red-500"
                            data-testid={`delete-inventory-${inv.id}`}
                          >
                            <Trash2 size={18} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">
              {editingInventory ? 'Edit Inventory' : 'Add Inventory'}
            </DialogTitle>
          </DialogHeader>
          
          {error && (
            <Alert variant="destructive" className="rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Inventory Name *</Label>
              <Input
                value={formData.inventory_name}
                onChange={(e) => setFormData({ ...formData, inventory_name: e.target.value })}
                placeholder="e.g., Main Warehouse or Van 1"
                className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="inventory-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Type</Label>
              <Select value={formData.type} onValueChange={(val) => setFormData({ ...formData, type: val })}>
                <SelectTrigger className="h-12 rounded-lg border-gray-300" data-testid="inventory-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse">
                    <div className="flex items-center gap-2">
                      <Warehouse size={16} className="text-[#0776BD]" />
                      Warehouse
                    </div>
                  </SelectItem>
                  <SelectItem value="vehicle">
                    <div className="flex items-center gap-2">
                      <Truck size={16} className="text-[#F0C500]" />
                      Vehicle
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {users.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Assigned Technician</Label>
                <Select 
                  value={formData.assigned_user_id || 'none'} 
                  onValueChange={(val) => setFormData({ ...formData, assigned_user_id: val === 'none' ? '' : val })}
                >
                  <SelectTrigger className="h-12 rounded-lg border-gray-300" data-testid="inventory-user-select">
                    <SelectValue placeholder="Not assigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not assigned</SelectItem>
                    {techUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-[#1F2937]">Active Status</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                data-testid="inventory-active-switch"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="save-inventory-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingInventory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">Delete Inventory</DialogTitle>
          </DialogHeader>
          <p className="text-[#6B7280]">Are you sure you want to delete "{itemToDelete?.inventory_name}"? This will remove all stock records for this location.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="rounded-lg" data-testid="confirm-delete-inventory-btn">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventories;
